// CAVE

import {
  joinHTML
} from '../../HTML.lib';

/**
 * Template for the subject line of a candidate representation email.
 *
 * @param {string} CANDIDATE__FULLNAME  [plaintext] The full name of the candidate.
 * @param {string} EMPLOYER__NAME  [plaintext] The name of the employer the candidate is interested in.
 */
export const TEMPLATE__CANDIDATE__CAVE__SUBJECT = `{{CANDIDATE__FULLNAME}} , thank you for your interest in {{EMPLOYER__NAME}}`;

/**
 * HTML template for emailing a candidate when a recruiter is interested.
 *
 * @constant {string} TEMPLATE__CANDIDATE__CAVE__BODY__DOT_IO
 *
 * @description
 * - Greets the candidate by first name.
 * - Introduces the recruiter and explains profile has been expedited.
 * - Confirms job role and employer with links.
 * - Provides candidate's email and phone.
 * - Explains partnership between recruiter and 10by10.
 * - Advises candidate on interview prep and main points of contact.
 * - Offers interview tips and how to reference their recruiter.
 * - Closes with a supportive signature.
 *
 * @templatePlaceholders
 * - {{CANDIDATE__NAME}}: Candidate first name (plaintext).
 * - {{CANDIDATE__EMAIL}}: Candidate email (plaintext).
 * - {{CANDIDATE__PHONE}}: Candidate phone number (plaintext).
 * - {{RECRUITER__NAME}}: Recruiter's name (plaintext).
 * - {{EMPLOYER__NAME}}: Employer name (plaintext).
 * - {{{EMPLOYER__LINK}}}: Hyperlink to employer website (HTML/text).
 * - {{{JOB__LINK}}}: Hyperlink to job description (HTML/text).
 * - {{JOB__TITLE}}: Job title (plaintext).
 * - {{{SIGNATURE}}}: Email sender signature (HTML/text).
 */
export const TEMPLATE__CANDIDATE__CAVE__BODY = joinHTML(`
Hi {{CANDIDATE__NAME}},
<br/><br/>
Great to connect! I'm Taylor from 10by10. {{RECRUITER__NAME}} connected us, and I was really impressed with your background and have expedited your profile to the hiring team. Just confirming:
<br/><br/>
Role: {{{JOB__LINK}}} at {{{EMPLOYER__LINK}}}<br/>
Email: {{CANDIDATE__EMAIL}}<br/>
Phone: {{CANDIDATE__PHONE}}
<br/><br/>
Our partnership combines {{RECRUITER__NAME}}’s candidate expertise with 10by10’s employer network to bring you additional great opportunities.
<br/><br/>
I’m representing you to {{EMPLOYER__NAME}}. {{RECRUITER__NAME}} is your main contact for interview prep and updates. We are both here to help you succeed in your interviews.
<br/><br/>
{{EMPLOYER__NAME}} may contact you directly for interviews. If you hear the good news first, please let us know so we can provide you interview tips!
<br/><br/>
First tip - Research the role or let us know your questions. {{EMPLOYER__NAME}} will focus the interview on your experience. Candidates who show genuine interest and are well-prepared tend to stand out and advance further.
<br/><br/>
Lastly, if asked about your recruiter, please refer to Taylor at 10by10 as your representative.
<br/><br/>
Looking forward to supporting your success!
<br/><br/>
Best regards,
<br/><br/>
{{{SIGNATURE}}}
`);

/**
 * Email template for notifying a candidate about expedited profile submission.
 *
 * @constant {string} TEMPLATE__CANDIDATE__CAVE__BODY__DOT_COM
 *
 * @description
 * - Greets the candidate by name.
 * - Confirms the job role and employer with links.
 * - Provides candidate's contact info (email, phone).
 * - Informs that the employer may contact them directly.
 * - Includes an initial interview tip emphasizing preparation and interest.
 * - Advises how to refer to their recruiter during interviews.
 * - Closes with a supportive signature.
 *
 * @templatePlaceholders
 * - {{CANDIDATE__NAME}}: Candidate's full name.
 * - {{{JOB__LINK}}}: Hyperlinked job title/role.
 * - {{{EMPLOYER__LINK}}}: Hyperlinked employer name.
 * - {{CANDIDATE__EMAIL}}: Candidate email.
 * - {{CANDIDATE__PHONE}}: Candidate phone number.
 * - {{EMPLOYER__NAME}}: Employer name.
 * - {{RECRUITER__NAME}}: Recruiter's name.
 * - {{{SIGNATURE}}}: Email signature block.
 */
export const TEMPLATE__CANDIDATE__CAVE__BODY__10BY10 = joinHTML(`
Hi {{CANDIDATE__NAME}},
<br/><br/>
I have expedited your profile to the hiring team. Just confirming:
<br/><br/>
Role: {{{JOB__LINK}}} at {{{EMPLOYER__LINK}}}<br/>
Email: {{CANDIDATE__EMAIL}}<br/>
Phone: {{CANDIDATE__PHONE}}
<br/><br/>
{{EMPLOYER__NAME}} may contact you directly for interviews. If you hear the good news first, please let us know so we can provide you interview tips!
<br/><br/>
First tip - Research the role or let us know your questions. {{EMPLOYER__NAME}} will focus the interview on your experience. Candidates who show genuine interest and are well-prepared tend to stand out and advance further.
<br/><br/>
Lastly, if asked about your recruiter, please refer to {{RECRUITER__NAME}} at 10by10 as your representative.
<br/><br/>
Looking forward to supporting your success!
<br/><br/>
Best regards,
<br/><br/>
{{{SIGNATURE}}}
`);
